import '../components/organisms/Tabs/tabs.scss'
import * as React from 'react'
import { useLocation } from '@reach/router'
import { graphql } from 'gatsby'
import ProductHeader from '../components/organisms/ProductHeader/ProductHeader'
import Breadcrumbs from '../components/organisms/Breadcrumbs/Breadcrumbs'
import { createProductSchema } from '../helpers/createProductSchema'
import ProductCarousel from '../components/organisms/GalleryCarousel/ProductCarousel'
import TextHeader from '../components/organisms/TextHeader/TextHeader'
import getTotalInventory from '../helpers/getTotalInventory'
import getOrderedRecommendedProductList from '../helpers/getOrderedRecommendedProductList'
import { createFAQSchema } from '../helpers/createFAQSchema'
import { createWebpageSchema } from '../helpers/createWebpageSchema'
import ArticleCarousel from '../components/organisms/GalleryCarousel/ArticleCarousel'
import SkeletonElement from '../components/organisms/Skeleton/Skeleton'
import FreeCourse from '../components/organisms/FreeCourse/FreeCourse'
import Accreditation from '../components/organisms/Accreditation/Accreditation'
import ProductTabs from '../components/organisms/ProductTabs/ProductTabs'
import MetaTwitterCard from '../components/organisms/MetaTwitterCard/MetaTwitterCard'
// ADD IF PRODUCT FEATURE TABLE IS NEEDED
// import ProductFeatureTable from '../components/organisms/ProductFeatureTable/ProductFeatureTable'

// Styled Component Declarations
//---------------------------------
// Gatsby Head
//---------------------------------
export function Head({ data, pageContext, location }) {
  const title = data.shopifyProduct.seo.title ?? data.shopifyProduct.title ?? null
  const description = data.shopifyProduct.seo.description ?? data.shopifyProduct.description ?? null

  const locationState = useLocation()
  const variantId = new URLSearchParams(locationState.search).get('variant')
  const selectedVariant = variantId
    ? data.shopifyProduct.variants.find(({ id }) => id === variantId)
    : null

  const hidden = data.shopifyProduct.tags.includes('Hidden')

  const productSchema = hidden
    ? null
    : createProductSchema(
        data.shopifyProduct,
        data?.contentfulPageProductInformation,
        selectedVariant,
        false
      )
  const faqSchema = hidden ? null : createFAQSchema(data.contentfulPageProductInformation?.faq)

  const webPageSchema = hidden
    ? null
    : createWebpageSchema({
        pageTitle: title,
        pageDescription: description,
        pageUrl: `${process.env.GATSBY_HELIGUY_DOMAIN}${location?.pathname}`,
        breadcrumbs: pageContext?.breadCrumbs,
      })

  const pageObjectUrl =
    data.shopifyProduct.featuredImage?.originalSrc || 'https://placehold.co/400x400'
  const pageObjectUrlWidth = data.shopifyProduct.featuredImage?.gatsbyImageData?.width || '400'
  const pageObjectUrlHeight = data.shopifyProduct.featuredImage?.gatsbyImageData?.height || '400'

  return (
    <>
      {/* Check for SEO Title */}
      {!title ? <title>No Title</title> : <title>{title}</title>}
      {/* Check for SEO Description */}
      {!description ? (
        <meta name="description" content="No Description" />
      ) : (
        <meta name="description" content={description} />
      )}
      <html lang="en" />
      <meta charset="UTF-8" />
      <meta name="keywords" content="" />
      <meta name="author" content="Heliguy" />
      <meta name="robots" content={hidden ? 'noindex' : 'index, follow'} />
      <meta property="og:locale" content="en" />
      <meta property="og:site_name" content="Heliguy™" />
      <meta property="og:type" content="website" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={pageObjectUrl} />
      <meta property="og:image:width" content={pageObjectUrlWidth} />
      <meta property="og:image:height" content={pageObjectUrlHeight} />
      <meta
        property="og:url"
        content={`${process.env.GATSBY_HELIGUY_DOMAIN}${location.pathname}`}
      />
      <MetaTwitterCard
        title={title}
        mainImage={pageObjectUrl}
        pageUrl={`${process.env.GATSBY_HELIGUY_DOMAIN}${location.pathname}`}
        pageDescription={description}
      />
      {faqSchema && <script type="application/ld+json">{faqSchema}</script>}
      {webPageSchema && <script type="application/ld+json">{webPageSchema}</script>}
      {productSchema && <script type="application/ld+json">{productSchema}</script>}
      <link rel="preconnect" href="https://static-tracking.klaviyo.com" crossOrigin />
      <link rel="preconnect" href="https://static.klaviyo.com" crossOrigin />
      <link rel="preconnect" href="https://fast.a.klaviyo.com" crossOrigin />
      <link rel="preconnect" href="https://static-forms.klaviyo.com" crossOrigin />
      <link rel="preconnect" href="https://heliguy.netlify.app" crossOrigin />
      <link rel="preconnect" href="https://heliguy.myshopify.com" crossOrigin />
      <link rel="preconnect" href="https://apis.google.com" crossOrigin />
      <link rel="preconnect" href="https://images.ctfassets.net" crossOrigin />
      <link
        rel="canonical"
        href={`${process.env.GATSBY_HELIGUY_DOMAIN}${pageContext.breadcrumb.location}`}
      />
    </>
  )
}

// Page Export
//---------------------------------
function ProductPageTemplate({ data, pageContext, location }) {
  // Breadcrumbs
  // ---------------------------------
  const isPartiallyActive = ({ isPartiallyCurrent, isCurrent }) =>
    isPartiallyCurrent && isCurrent
      ? { className: 'breadcrumb__link breadcrumb__link__active' }
      : {}

  // Rich Content
  //---------------------------------
  let product_description
  let product_specification
  let product_whatsInTheBox
  let product_faq
  let product_documents
  let product_overview
  let product_heliguyAssist

  const changeVariant = (newVariant) => {
    if (typeof window !== 'undefined') {
      window.history.pushState(
        {},
        'Changed variant',
        `${location.origin}${location.pathname}?variant=${newVariant}`
      )
    }
  }

  const searchVariantParam = new URLSearchParams(location.search).get('variant')
  const selectedVariant =
    searchVariantParam &&
    data.shopifyProduct.variants.find(
      (variant) => variant?.shopifyId === `gid://shopify/ProductVariant/${searchVariantParam}`
    )

  if (data.contentfulPageProductInformation) {
    product_description = data.contentfulPageProductInformation.productGeneralDescription
    product_specification =
      data.contentfulPageProductInformation.productSpecification?.productSpecification

    const wbText = data.contentfulPageProductInformation.whatsInTheBoxList
    const wbImage = data.contentfulPageProductInformation.whatInTheBoxImage?.gatsbyImageData
    product_whatsInTheBox = { text: wbText, image: wbImage }

    product_faq = data.contentfulPageProductInformation.faq
    product_documents = data.contentfulPageProductInformation.documentationSupport
    product_overview = data.contentfulPageProductInformation.supportingContent
    product_heliguyAssist = data?.contentfulPageProductInformation?.heliguyAssist || []
  }

  const isCtaTextDisplayed = data?.contentfulPageProductInformation?.isCtaTextDisplayed
  const ctaTextBox = data?.contentfulPageProductInformation?.ctaTextBox || ''

  const recommendedProductList =
    data?.recommendedProductList?.edges.map(({ node }) => ({ ...node })) || []
  const recommendedProductSectionTitle =
    data?.contentfulPageProductInformation?.recommendedProductSectionTitle || ''
  const recommendedProductSectionSubtitle =
    data?.contentfulPageProductInformation?.recommendedProductSectionSubtitle || ''
  const recommendedContentfulProductsList =
    data.contentfulPageProductInformation?.recommendedProductList || []
  const productsShopifyMetafields = data?.shopifyProduct?.metafields || []
  const recommendedProductsFromShopify = productsShopifyMetafields.find(
    ({ key }) => key === 'recommended'
  )
  const relatedBlogs = data?.contentfulPageProductInformation?.relatedBlogs

  const orderedRecommendedProductList = getOrderedRecommendedProductList({
    productArray: recommendedProductList,
    orderArray: [
      ...recommendedContentfulProductsList.map(({ productHandle }) => productHandle),
      ...(recommendedProductsFromShopify ? recommendedProductsFromShopify.value.split(',') : []),
    ],
  })

  const rentalMetaFields = data.shopifyProduct.metafields.filter((metafield) =>
    ['json_config', 'link', 'rrp', 'accessories', 'training_text', 'training_title'].includes(
      metafield.key
    )
  )

  const relateProductsIdsArray = JSON.parse(
    data.shopifyProduct.metafields.find(
      ({ key, namespace }) => key === 'related_products' && namespace === 'custom'
    )?.value || '[]'
  )

  const totalInventory = data?.shopifyProduct?.totalInventory || 0

  const productVariants = data.shopifyProduct?.variants || []

  const upgradeButtonText =
    data.shopifyProduct.metafields.find(({ key }) => key === 'combo_button_text')?.value || ''

  const upgradeButtonLink =
    data.shopifyProduct.metafields.find(({ key }) => key === 'combo_link')?.value || ''

  const courseId = data.shopifyProduct?.courseId?.value || ''

  const image =
    data.shopifyProduct.featuredImage === null
      ? {
          url: 'https://placehold.co/400x400',
          alt: 'no image present',
        }
      : {
          url: data.shopifyProduct.featuredImage.gatsbyImageData,
          alt: data.shopifyProduct.featuredImage.altText,
        }

  const isFreeCourseProduct = data?.shopifyProduct?.handle === 'dji-zenmuse-l1-workflow-course'

  const freeCourseRef = React.useRef()

  const bonusItems = JSON.parse(data.shopifyProduct?.bonusItems?.value || '[]')

  return (
    <div className="templateWrapper templateWrapper--alt">
      <section className="outer margin-160">
        <div className="inner">
          <Breadcrumbs
            // disableLinks={['/products/']}
            crumbs={pageContext.breadCrumbs}
            crumbSeparator=" - "
            // crumbLabel={data.shopifyProduct.title}
            getProps={isPartiallyActive}
          />
          <ProductHeader
            bonusItems={bonusItems}
            productTitle={data.shopifyProduct.title}
            productType={data?.shopifyProduct?.productType || ''}
            productId={data.shopifyProduct.id}
            shopifyId={data?.shopifyProduct?.shopifyId}
            productDescriptionShopify={data.shopifyProduct.descriptionHtml}
            variants={data.shopifyProduct.variants}
            totalCount={getTotalInventory({ totalInventory, productVariants })}
            tags={data.shopifyProduct.tags}
            isInventoryTracks={data.shopifyProduct.tracksInventory}
            productDescriptionContentful={product_description}
            imgSrc={image.url}
            altTag={image.alt}
            productImages={data.shopifyProduct.media}
            rentalMetaFields={rentalMetaFields}
            relateProductsIdsArray={relateProductsIdsArray}
            courseId={courseId}
            upgradeButtonText={upgradeButtonText}
            upgradeButtonLink={upgradeButtonLink}
            changeVariant={changeVariant}
            variant={selectedVariant}
            location={location}
            breadCrumbs={pageContext.breadCrumbs}
            isFreeCourseProduct={isFreeCourseProduct}
            freeCourseRef={freeCourseRef}
            isCtaTextDisplayed={isCtaTextDisplayed}
            ctaTextBox={ctaTextBox}
          />
        </div>
      </section>
      {data.contentfulPageProductInformation ? (
        <ProductTabs
          faq={product_faq}
          documents={product_documents}
          heliguyAssist={product_heliguyAssist}
          isRental={false}
          overview={product_overview}
          specification={product_specification}
          whatsInTheBox={product_whatsInTheBox}
        />
      ) : null}
      {orderedRecommendedProductList.length > 0 && (
        <section className="outer margin-160">
          <div className="inner">
            <TextHeader
              title={recommendedProductSectionTitle || 'Products that compliment this product'}
              subtitle={recommendedProductSectionSubtitle}
            />
            <ProductCarousel carouselImages={orderedRecommendedProductList} nonFooter />
          </div>
        </section>
      )}
      {relatedBlogs && relatedBlogs.length > 0 && (
        <section className="outer margin-160">
          <div className="inner">
            <TextHeader
              title="Related Posts"
              subtitle="Learn more with our in-depth industry content"
            />
            <ArticleCarousel carouselImages={relatedBlogs} />
          </div>
        </section>
      )}
      {isFreeCourseProduct && <FreeCourse sectionRef={freeCourseRef} />}
      {/* ADD IF PRODUCT FEATURE TABLE IS NEEDED */}
      {/* <div className="margin-160"> */}
      {/* <CompanyReviews /> */}
      <Accreditation />
      {/* ADD IF PRODUCT FEATURE TABLE IS NEEDED */}
      {/* </div>
        <section className="outer">
          <div className="inner">
            <ProductFeatureTable />
          </div>
        </section> */}
    </div>
  )
}

export default ProductPageTemplate

// GraphQL Call
//---------------------------------
export const data = graphql`
  query ($id: String!, $handle: String!, $recommendedProductHandleArray: [String]) {
    shopifyProduct(id: { eq: $id }) {
      seo {
        description
        title
      }
      metafields {
        id
        value
        key
        namespace
      }
      metafield(key: "excerpt", namespace: "content") {
        id
        value
      }
      bonusItems: metafield(key: "product_bonus_items", namespace: "custom") {
        value
        type
      }
      courseId: metafield(key: "course_id", namespace: "training") {
        value
        type
      }
      id
      shopifyId
      title
      tags
      descriptionHtml
      tracksInventory
      totalInventory
      hasOnlyDefaultVariant
      featuredImage {
        gatsbyImageData(formats: WEBP, width: 720)
        altText
        originalSrc
      }
      media {
        id
        alt
        mediaContentType
        preview {
          image {
            altText
            gatsbyImageData(formats: WEBP, width: 720)
            originalSrc
          }
        }
        ... on ShopifyVideo {
          sources {
            format
            url
            mimeType
          }
        }
        ... on ShopifyExternalVideo {
          host
          embeddedUrl
          preview {
            image {
              altText
              gatsbyImageData(formats: WEBP, width: 720)
            }
          }
        }
      }
      variants {
        id
        shopifyId
        title
        weight
        weightUnit
        price
        compareAtPrice
        barcode
        sku
        availableForSale
        inventoryQuantity
        image {
          gatsbyImageData(formats: WEBP, width: 720)
          altText
          originalSrc
        }
        courseId: metafield(key: "course_id", namespace: "training") {
          value
          type
        }
      }
      hasOnlyDefaultVariant
      totalInventory
      vendor
      handle
      description
      productType
      priceRangeV2 {
        maxVariantPrice {
          amount
          currencyCode
        }
      }
      collections {
        title
        metafields {
          key
          value
        }
      }
    }
    contentfulPageProductInformation(productHandle: { eq: $handle }) {
      id
      pageTitle
      __typename
      contentful_id
      productGeneralDescription {
        raw
      }
      faq {
        contentful_id
        faqQuestion
        faqAnswer {
          raw
        }
      }
      productSpecification {
        productSpecification
      }
      supportingContent {
        contentful_id
        title
        subtitle
        direction
        body {
          raw
        }
        image {
          gatsbyImageData
          description
        }
        video {
          file {
            url
          }
        }
      }
      documentationSupport {
        contentful_id
        file {
          fileName
          url
          details {
            size
          }
        }
        title
      }
      recommendedProductSectionTitle
      recommendedProductSectionSubtitle
      recommendedProductList {
        ... on Node {
          ... on ContentfulPageProductInformation {
            productHandle
          }
          ... on ContentfulPageRentalProductInformation {
            productHandle
          }
        }
      }
      whatsInTheBoxList {
        raw
      }
      whatInTheBoxImage {
        gatsbyImageData(width: 2000)
      }
      heliguyAssist {
        contentful_id
        faqQuestion
        faqAnswer {
          raw
        }
      }
      relatedBlogs {
        id
        pageSlug
        articleTitle
        articleCategory
        updatedAt
        posterImage {
          url
          description
          gatsbyImageData(formats: WEBP, width: 1000)
        }
      }
      isCtaTextDisplayed
      ctaTextBox
    }
    recommendedProductList: allShopifyProduct(
      filter: { handle: { in: $recommendedProductHandleArray } }
    ) {
      edges {
        node {
          title
          shopifyId
          handle
          productType
          priceRangeV2 {
            minVariantPrice {
              amount
              currencyCode
            }
          }
          metafield(key: "excerpt", namespace: "content") {
            id
            value
          }
          featuredImage {
            gatsbyImageData(formats: WEBP, width: 900)
            altText
          }
          metafields {
            key
            value
          }
        }
      }
    }
  }
`
